import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { Modal } from "bootstrap"

const MAX_FILE_SIZE = 100 * 1024 * 1024
const ACCEPTED_EXTENSIONS = [".pdf", ".ppt", ".pptx"]

export default class extends Controller {
  static targets = [
    "addModal", "addForm", "addTitle", "addFileInput", "addSignedId", "addFilename",
    "addSubmit", "addPreview", "addError", "addDropzone", "addFileRow", "addFileName",
    "editModal", "editForm", "editTitle", "editDescription", "editTag", "editLink",
    "editFileInput", "editSignedId", "editFilename", "editSubmit", "editPreview",
    "editError", "editDropzone", "editFileRow", "editFileName", "editRole"
  ]
  static values = { directUploadUrl: String }

  connect() {
    this.addObjectUrl = null
    this.editObjectUrl = null
    this.editExistingPreviewUrl = ""
    this.editHasExistingFile = false
    this.editFileRemoved = false
    this.editInitialState = null
    this.submittedAdd = false
    this.submittedEdit = false

    this.boundAddHidden = () => this.resetAdd()
    this.boundEditHidden = () => this.resetEdit()
    this.addModalTarget.addEventListener("hidden.bs.modal", this.boundAddHidden)
    this.editModalTarget.addEventListener("hidden.bs.modal", this.boundEditHidden)
    this.refreshAddActions()
  }

  disconnect() {
    this.revokeObjectUrl("add")
    this.revokeObjectUrl("edit")
    this.addModalTarget.removeEventListener("hidden.bs.modal", this.boundAddHidden)
    this.editModalTarget.removeEventListener("hidden.bs.modal", this.boundEditHidden)
  }

  refreshAddActions() {
    const hasFile = Boolean(this.addFile())
    this.addSubmitTarget.disabled = !(this.addTitleTarget.value.trim() && hasFile)
    this.addPreviewTarget.disabled = !hasFile
  }

  refreshEditActions() {
    const hasFile = this.editHasFile()
    const dirty = this.editState() !== this.editInitialState
    this.editSubmitTarget.disabled = !(dirty && this.editTitleTarget.value.trim() && hasFile)
    this.editPreviewTarget.disabled = !hasFile
  }

  browseAdd(event) {
    event.preventDefault()
    this.addFileInputTarget.click()
  }

  browseEdit(event) {
    event.preventDefault()
    this.editFileInputTarget.click()
  }

  addFileChanged() {
    this.applySelectedFile("add", this.addFileInputTarget.files[0])
  }

  editFileChanged() {
    this.applySelectedFile("edit", this.editFileInputTarget.files[0])
  }

  dropAdd(event) {
    this.dropOn(event, "add")
  }

  dropEdit(event) {
    this.dropOn(event, "edit")
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("is-dragover")
  }

  dragLeave(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    event.currentTarget.classList.remove("is-dragover")
  }

  removeAddFile(event) {
    event.preventDefault()
    this.clearFile("add")
    this.refreshAddActions()
  }

  removeEditFile(event) {
    event.preventDefault()
    this.editFileRemoved = true
    this.editHasExistingFile = false
    this.clearFile("edit")
    this.showEditFile(false)
    this.refreshEditActions()
  }

  openEdit(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.resetEdit()

    this.editFormTarget.action = button.dataset.url
    this.editTitleTarget.value = button.dataset.title || ""
    this.editDescriptionTarget.value = button.dataset.description || ""
    this.editTagTarget.value = button.dataset.resourceTag || ""
    this.editLinkTarget.value = button.dataset.linkUrl || ""
    this.editExistingPreviewUrl = button.dataset.previewUrl || ""
    this.editFilenameTarget.value = button.dataset.filename || ""
    this.editFileNameTarget.textContent = button.dataset.filename || ""
    this.editHasExistingFile = Boolean(button.dataset.filename)
    this.editFileRemoved = false

    const roles = (button.dataset.roles || "").split(" ").filter(Boolean)
    this.editRoleTargets.forEach((checkbox) => {
      checkbox.checked = roles.includes(checkbox.value)
    })

    this.showEditFile(this.editHasExistingFile)
    this.editInitialState = this.editState()
    this.refreshEditActions()
    Modal.getOrCreateInstance(this.editModalTarget).show()
  }

  toggleRole(event) {
    const remaining = this.selectedRoles()
    if (remaining.length === 0) {
      event.currentTarget.checked = true
      this.showError("edit", "Choose who can see this resource.")
      return
    }

    this.hideError("edit")
    this.refreshEditActions()
  }

  previewAdd(event) {
    event.preventDefault()
    this.openPreview(this.addObjectUrl)
  }

  previewEdit(event) {
    event.preventDefault()
    this.openPreview(this.editObjectUrl || this.editExistingPreviewUrl)
  }

  submitAdd(event) {
    if (this.submittedAdd) return

    event.preventDefault()
    this.hideError("add")

    const file = this.addFile()
    if (!this.addTitleTarget.value.trim() || !file) {
      this.showError("add", "Enter a title and attach a file to publish.")
      return
    }

    this.uploadAndSubmit("add", file)
  }

  submitEdit(event) {
    if (this.submittedEdit) return

    event.preventDefault()
    event.stopPropagation()
    this.hideError("edit")

    if (!this.editTitleTarget.value.trim()) {
      this.showError("edit", "A title is required.")
      return
    }

    if (!this.editHasFile()) {
      this.showError("edit", "Attach a PDF or PPTX file.")
      return
    }

    if (this.selectedRoles().length === 0) {
      this.showError("edit", "Choose who can see this resource.")
      return
    }

    const file = this.editFile()
    if (file) {
      this.uploadAndSubmit("edit", file)
      return
    }

    this.submittedEdit = true
    this.editFormTarget.submit()
  }

  applySelectedFile(mode, file) {
    const dropzone = mode === "add" ? this.addDropzoneTarget : this.editDropzoneTarget
    dropzone.classList.remove("is-dragover")

    if (!file) return

    const error = this.fileError(file)
    if (error) {
      this.showError(mode, error)
      this.clearFileInput(mode)
      return
    }

    this.hideError(mode)
    this.revokeObjectUrl(mode)
    const objectUrl = URL.createObjectURL(file)

    if (mode === "add") {
      this.addObjectUrl = objectUrl
      this.addFilenameTarget.value = file.name
      this.addFileNameTarget.textContent = file.name
      this.addDropzoneTarget.hidden = true
      this.addFileRowTarget.hidden = false
      this.refreshAddActions()
      return
    }

    this.editObjectUrl = objectUrl
    this.editFileRemoved = false
    this.editHasExistingFile = true
    this.editFilenameTarget.value = file.name
    this.editFileNameTarget.textContent = file.name
    this.showEditFile(true)
    this.refreshEditActions()
  }

  dropOn(event, mode) {
    event.preventDefault()
    const file = event.dataTransfer?.files?.[0]
    const input = mode === "add" ? this.addFileInputTarget : this.editFileInputTarget
    if (!file) return

    const transfer = new DataTransfer()
    transfer.items.add(file)
    input.files = transfer.files
    this.applySelectedFile(mode, file)
  }

  uploadAndSubmit(mode, file) {
    const submitButton = mode === "add" ? this.addSubmitTarget : this.editSubmitTarget
    submitButton.disabled = true

    const upload = new DirectUpload(file, this.directUploadUrlValue)
    upload.create((error, blob) => {
      if (error) {
        submitButton.disabled = false
        this.showError(mode, "The file could not be uploaded. Try again.")
        return
      }

      if (mode === "add") {
        this.addSignedIdTarget.value = blob.signed_id
        this.addFilenameTarget.value = blob.filename
        this.submittedAdd = true
        this.addFormTarget.submit()
        return
      }

      this.editSignedIdTarget.value = blob.signed_id
      this.editFilenameTarget.value = blob.filename
      this.submittedEdit = true
      this.editFormTarget.submit()
    })
  }

  fileError(file) {
    if (!this.isAcceptedFile(file)) return "Upload a PDF or PPTX file."
    if (file.size > MAX_FILE_SIZE) return "File must be 100 MB or smaller."
    return null
  }

  isAcceptedFile(file) {
    const name = (file.name || "").toLowerCase()
    return ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  }

  addFile() {
    return this.addFileInputTarget.files[0]
  }

  editFile() {
    return this.editFileInputTarget.files[0]
  }

  editHasFile() {
    return Boolean(this.editFile() || (this.editHasExistingFile && !this.editFileRemoved))
  }

  selectedRoles() {
    return this.editRoleTargets.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value)
  }

  editState() {
    return JSON.stringify({
      title: this.editTitleTarget.value.trim(),
      description: this.editDescriptionTarget.value.trim(),
      tag: this.editTagTarget.value.trim(),
      link: this.editLinkTarget.value.trim(),
      roles: this.selectedRoles().slice().sort(),
      filename: this.editFilenameTarget.value,
      signedId: this.editSignedIdTarget.value,
      removed: this.editFileRemoved
    })
  }

  showEditFile(hasFile) {
    this.editFileRowTarget.hidden = !hasFile
    this.editDropzoneTarget.hidden = hasFile
  }

  clearFile(mode) {
    this.clearFileInput(mode)
    this.revokeObjectUrl(mode)

    if (mode === "add") {
      this.addSignedIdTarget.value = ""
      this.addFilenameTarget.value = ""
      this.addFileNameTarget.textContent = ""
      this.addDropzoneTarget.hidden = false
      this.addFileRowTarget.hidden = true
      return
    }

    this.editSignedIdTarget.value = ""
    this.editFilenameTarget.value = ""
    this.editFileNameTarget.textContent = ""
    this.editExistingPreviewUrl = ""
  }

  clearFileInput(mode) {
    const input = mode === "add" ? this.addFileInputTarget : this.editFileInputTarget
    input.value = ""
  }

  resetAdd() {
    this.submittedAdd = false
    this.addFormTarget.reset()
    this.clearFile("add")
    this.hideError("add")
    this.refreshAddActions()
  }

  resetEdit() {
    this.submittedEdit = false
    this.editFormTarget.reset()
    this.editFileRemoved = false
    this.editHasExistingFile = false
    this.editExistingPreviewUrl = ""
    this.editInitialState = null
    this.clearFile("edit")
    this.showEditFile(false)
    this.hideError("edit")
    this.refreshEditActions()
  }

  openPreview(url) {
    if (!url) return
    window.open(url, "_blank", "noopener,noreferrer")
  }

  revokeObjectUrl(mode) {
    if (mode === "add" && this.addObjectUrl) {
      URL.revokeObjectURL(this.addObjectUrl)
      this.addObjectUrl = null
    }

    if (mode === "edit" && this.editObjectUrl) {
      URL.revokeObjectURL(this.editObjectUrl)
      this.editObjectUrl = null
    }
  }

  showError(mode, message) {
    const target = mode === "add" ? this.addErrorTarget : this.editErrorTarget
    target.textContent = message
    target.hidden = false
  }

  hideError(mode) {
    const target = mode === "add" ? this.addErrorTarget : this.editErrorTarget
    target.hidden = true
    target.textContent = ""
  }
}
